import * as React from 'react';

import {
  styled,
  styledDiv,
  emotionStyled,
  tokens,
} from '@packages/utilities/styled';

import {Subheading} from '@packages/systems/design-system/Text';
import Hitarea from '@packages/systems/design-system/Hitarea';
import {
  updateExpressionAtAction,
  extendExpressionEditorActionAtPath,
  ExpressionBinding,
  ExpressionEditor,
  getBindingLabel,
  type ExpressionEditorDispatch,
} from '@packages/systems/ExpressionEditor-utils';
import {
  BINDING_SELECT_BUTTON_HEIGHT,
  ArgumentSettings,
} from '@packages/systems/ExpressionEditor-utils-ui';
import {usePlugins} from '@packages/utilities/platform';
import * as w from '@packages/systems/wfdl';
import {
  type ContextualExpression,
  ContextualExpressions,
  getAnn,
  stripAll,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {EHole, Expression} from '@packages/systems/wfdl-ast';
import {
  findAvailableBindings,
  hasAvailableBindings,
  replaceExprWithHoleAndReInfer,
} from '@packages/systems/wfdl-findBindings';
import SelectButton from '@packages/systems/design-system/SelectButton';
import TooltipControl from '@packages/systems/design-system/TooltipControl';

import {TBoolean} from '@packages/systems/plugins-utils/Boolean';
import {StyledHelpIcon} from '@packages/systems/plugins/Effect';
import {LogicLabel} from '@packages/systems/flows/shared';
import {VisuallyHidden} from '@packages/systems/spring';

const GridWrapper = emotionStyled('div')({
  display: 'grid',
  gridRowGap: tokens.space[2],
});
const FlexWrapper = emotionStyled('div')({
  display: 'flex',
  flexDirection: 'row',
});

const BindingWrapper = emotionStyled('div')({
  flex: 1,
});

const StyledSelectButton = styled(SelectButton)({
  height: BINDING_SELECT_BUTTON_HEIGHT,
});

const StyledLabel = styled(LogicLabel)(({theme: {gutters}}) => ({
  display: 'grid',
  gridTemplateColumns: `max-content ${gutters.x16}px`,
  gridColumnGap: gutters.x4,
  alignItems: 'center',
}));

const TooltipContent = styledDiv(({theme: {gutters}}) => ({
  maxWidth: gutters.x4 * 56,
}));

const justifyCenter = {justifySelf: 'center'} as const;

const tip =
  'Set the condition for the True branch. ' +
  'If this condition is not met, the actions in the False branch will run.';

type GetPathForDispatch = (
  nestedExpr: ContextualExpression
) => w.ExpressionPath;

export const PredicatedBoolean = w.TypeImplementation.Alias({
  type: TBoolean,
  render: function RenderPredicatedBoolean({expr, dispatch}) {
    const call = ContextualExpressions.deconstructCall(expr);
    const [fieldArg, predicateArg] = call.args;
    const {expressionPath} = getAnn(expr).env;

    const getPathForDispatch: GetPathForDispatch = React.useCallback(
      function getPathForDispatch(nestedExpr: ContextualExpression) {
        return getAnn(nestedExpr).env.expressionPath.slice(
          expressionPath.length
        );
      },
      [expressionPath]
    );

    return (
      <GridWrapper>
        <Subheading>Condition</Subheading>

        <StyledLabel label="Run the true branch if... ">
          {/* @ts-expect-error - TS2739 - Type '{ style: { readonly justifySelf: "center"; }; icon: Element; content: Element; alternate: true; }' is missing the following properties from type '{ [x: string]: any; iconStyle?: { padding: number; boxSizing: string; } | undefined; icon?: Element | undefined; cursor?: string | undefined; placement?: string | undefined; children: any; hideOnClick: any; style: any; automationLabel?: string | undefined; }': children, hideOnClick */}
          <TooltipControl
            style={justifyCenter}
            icon={<StyledHelpIcon />}
            content={<TooltipContent>{tip}</TooltipContent>}
            alternate
          />
        </StyledLabel>

        <SelectField
          // @ts-expect-error - TS2322 - Type 'AnnotatedExpression<ContextualExpressionAnnotation> | undefined' is not assignable to type 'ContextualExpression'.
          expr={fieldArg}
          dispatch={dispatch}
          getPathForDispatch={getPathForDispatch}
        />
        <SelectPredicate
          // @ts-expect-error - TS2322 - Type 'AnnotatedExpression<ContextualExpressionAnnotation> | undefined' is not assignable to type 'ContextualExpression'.
          expr={predicateArg}
          dispatch={dispatch}
          getPathForDispatch={getPathForDispatch}
        />
        <VisuallyHidden id="set-condition-description">{tip}</VisuallyHidden>
      </GridWrapper>
    );
  },
});

function SelectField({
  expr,
  dispatch,
  getPathForDispatch,
}: {
  expr: ContextualExpression;
  dispatch: (arg1: any) => void;
  getPathForDispatch: (arg1: ContextualExpression) => w.ExpressionPath;
}) {
  const plugins = usePlugins();
  const [rootCheckerState, rootContextualExpr] = React.useContext(
    w.TypeCheckerState
  );

  if (rootContextualExpr === null) {
    return null;
  }

  const {fun, args} = ContextualExpressions.deconstructCall(expr);
  const path = getPathForDispatch(expr);
  const currPath = getAnn(expr).env.expressionPath;

  const argumentSettings = ArgumentSettings({
    fun,
    args,
    dispatch: extendExpressionEditorActionAtPath(dispatch)(path),
    label: getBindingLabel(getAnn(fun).env.context.values, stripAll(fun)),
  });

  // We get a new state for the type checker and the contextual expression
  // to continue rendering bindings of other data types for the predicate field
  const [
    rootCheckerStateWithHoleAtPath,
    rootContextualExpressionWithHoleAtPath,
  ] = replaceExprWithHoleAndReInfer(
    rootContextualExpr,
    currPath,
    rootCheckerState
  );

  const dispatchBindingUpdate = (binding: null | Expression) => {
    dispatch(updateExpressionAtAction(binding || EHole(), path));
  };

  if (ContextualExpressions.isHole(stripAnn(expr))) {
    const bindings = findAvailableBindings(expr, rootCheckerState, plugins);

    const children = hasAvailableBindings(bindings) ? null : (
      <StyledSelectButton>No values to select</StyledSelectButton>
    );

    return (
      <ExpressionBinding
        expression={expr}
        onChange={dispatchBindingUpdate}
        button={(openPopover, targetRef) => (
          <span ref={targetRef}>
            <Hitarea aria-describedby="set-condition-description">
              <StyledSelectButton onClick={openPopover} indicator>
                Select field...
              </StyledSelectButton>
            </Hitarea>
          </span>
        )}
        bindUIVariant="custom"
      >
        {children}
      </ExpressionBinding>
    );
  }

  return (
    <FlexWrapper>
      <w.TypeCheckerState.Provider
        value={[
          rootCheckerStateWithHoleAtPath,
          rootContextualExpressionWithHoleAtPath,
        ]}
      >
        <BindingWrapper>
          <ExpressionBinding
            expression={expr}
            onChange={dispatchBindingUpdate}
            bindUIVariant="dot"
            hasArguments={argumentSettings !== null}
          />
        </BindingWrapper>
      </w.TypeCheckerState.Provider>
      {argumentSettings}
    </FlexWrapper>
  );
}

function SelectPredicate({
  expr,
  dispatch,
  getPathForDispatch,
}: {
  expr: ContextualExpression;
  dispatch: ExpressionEditorDispatch;
  getPathForDispatch: GetPathForDispatch;
}) {
  if (ContextualExpressions.isHole(stripAnn(expr))) {
    return (
      <ExpressionBinding
        expression={expr}
        onChange={(binding) => {
          dispatch(
            updateExpressionAtAction(
              binding || EHole(),
              getPathForDispatch(expr)
            )
          );
        }}
        button={(openPopover, targetRef) => (
          <span ref={targetRef}>
            <Hitarea aria-describedby="set-condition-description">
              <StyledSelectButton onClick={openPopover} indicator>
                Select logic...
              </StyledSelectButton>
            </Hitarea>
          </span>
        )}
        bindUIVariant="custom"
      />
    );
  }

  const call = ContextualExpressions.deconstructCall(expr);

  return (
    <>
      <ExpressionEditor
        expression={call.fun}
        dispatch={(action) => {
          const path = getPathForDispatch(
            // Reset the call expr if user is "unlinking"
            !('binding' in action) || action.binding == null ? expr : call.fun
          );

          extendExpressionEditorActionAtPath(dispatch)(path)(action);
        }}
      />

      {call.args.map((arg, i) => (
        <ExpressionEditor
          key={`${i}-call-predicate`}
          expression={arg}
          dispatch={extendExpressionEditorActionAtPath(dispatch)(
            getPathForDispatch(arg)
          )}
        />
      ))}
    </>
  );
}
